import { useCallback, useEffect, useRef, useState } from 'react';
import { AiOutlineCloudUpload, AiOutlineFileExcel } from 'react-icons/ai';
import { PrimaryButton, SecondaryButton } from '../../../shared/ui/Inputs.js';
import { Spinner } from '../../../shared/ui/Spinners.js';
import { BrighterGreen } from '../../../app/theme/tokens/colors.js';
import { SpreadsheetUploadValidationModal } from '../components/SpreadsheetUploadValidationModal';
import { SimplePopup } from '../../../shared/overlays/index.js';
import { bulkSapProductBranch } from '../../../services/configuration/sapProductBranchService.js';
import {
    CardTitle,
    DropHint,
    DropIcon,
    DropText,
    DropTitle,
    DropZone,
    DropZoneInner,
    EmptyPreview,
    FileName,
    FilePill,
    FileSize,
    HiddenInput,
    MetaRow,
    PreCheckBox,
    PreCheckError,
    PreviewMeta,
    PreviewTable,
    PreviewTableWrap,
    formatBytes,
} from './spreadsheetUploadConfig.style.js';
import {
    buildPrecheckFromArrayBuffer,
    parseProductsFromArrayBuffer,
    validateRecordsForUpload,
} from './SapProductBranchUploadParser.js';

// ─── constantes ─────────────────────────────────────────────────────────────

const ACCEPT =
    '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

// ─── componente ─────────────────────────────────────────────────────────────

export const SapProductBranchUploadModal = ({ onClose, onSuccess }) => {
    const fileInputRef = useRef(null);
    const validatedRecordsRef = useRef(null);

    const [selectedFile, setSelectedFile] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [parseError, setParseError] = useState('');
    const [precheckProgress, setPrecheckProgress] = useState({
        stage: '',
        current: 0,
        total: 0,
    });
    const [preCheck, setPreCheck] = useState({
        sheetName: '',
        headers: [],
        rows: [],
        totalRows: 0,
    });

    const [validationOpen, setValidationOpen] = useState(false);
    const [missingColumns, setMissingColumns] = useState([]);
    const [validationMeta, setValidationMeta] = useState({
        fileName: '',
        sheetName: '',
    });

    const [isSending, setIsSending] = useState(false);
    const [sendErrors, setSendErrors] = useState([]);
    const [sendResult, setSendResult] = useState(null);

    // ─── reset ────────────────────────────────────────────────────────────────

    const resetSelection = useCallback(() => {
        setSelectedFile(null);
        setParseError('');
        setIsParsing(false);
        setPrecheckProgress({ stage: '', current: 0, total: 0 });
        validatedRecordsRef.current = null;
        setPreCheck({ sheetName: '', headers: [], rows: [], totalRows: 0 });
        setMissingColumns([]);
        setValidationOpen(false);
        setValidationMeta({ fileName: '', sheetName: '' });
        setSendErrors([]);
        setSendResult(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    // ─── seleção de arquivo ───────────────────────────────────────────────────

    const onSelectFile = useCallback((file) => {
        if (!file) return;
        setSendErrors([]);
        setSendResult(null);
        setValidationMeta({ fileName: file?.name || '', sheetName: '' });
        setSelectedFile(file);
    }, []);

    // ─── parse ao selecionar arquivo ─────────────────────────────────────────

    useEffect(() => {
        if (!selectedFile) {
            setIsParsing(false);
            setParseError('');
            setPrecheckProgress({ stage: '', current: 0, total: 0 });
            validatedRecordsRef.current = null;
            setPreCheck({ sheetName: '', headers: [], rows: [], totalRows: 0 });
            return;
        }

        let cancelled = false;
        const reader = new FileReader();

        setIsParsing(true);
        setParseError('');
        setPrecheckProgress({ stage: 'Lendo planilha…', current: 0, total: 0 });
        validatedRecordsRef.current = null;
        setPreCheck({ sheetName: '', headers: [], rows: [], totalRows: 0 });
        setMissingColumns([]);
        setValidationOpen(false);

        reader.onload = async (event) => {
            try {
                if (cancelled) return;
                const arrayBuffer = event?.target?.result;
                if (!arrayBuffer) throw new Error('Não foi possível ler o arquivo.');

                const result = buildPrecheckFromArrayBuffer({
                    arrayBuffer,
                    previewRowCount: 3,
                });

                setValidationMeta({
                    fileName: selectedFile?.name || '',
                    sheetName: result.sheetName,
                });

                if (result.headers.length === 0 && result.totalRows === 0) {
                    setPreCheck({
                        sheetName: result.sheetName,
                        headers: [],
                        rows: [],
                        totalRows: 0,
                    });
                    return;
                }

                setMissingColumns(result.missingColumns);
                setValidationOpen(true);

                if (result.missingColumns.length > 0) {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                }

                setPreCheck({
                    sheetName: result.sheetName,
                    headers: result.headers,
                    rows: result.rows,
                    totalRows: result.totalRows,
                });

                setPrecheckProgress({
                    stage: 'Validando linhas…',
                    current: 0,
                    total: result.totalRows,
                });

                const parsed = parseProductsFromArrayBuffer({ arrayBuffer });

                await validateRecordsForUpload({
                    records: parsed.records,
                    maxErrors: 20,
                    onProgress: ({ current, total }) =>
                        setPrecheckProgress({
                            stage: 'Validando linhas…',
                            current,
                            total,
                        }),
                });

                validatedRecordsRef.current = parsed.records;
                setPrecheckProgress({ stage: '', current: 0, total: 0 });
            } catch (err) {
                validatedRecordsRef.current = null;
                setParseError(err?.message || 'Falha ao ler a planilha.');
            } finally {
                if (!cancelled) setIsParsing(false);
            }
        };

        reader.onerror = () => {
            if (cancelled) return;
            setIsParsing(false);
            setParseError('Falha ao ler o arquivo.');
            setPrecheckProgress({ stage: '', current: 0, total: 0 });
            validatedRecordsRef.current = null;
        };

        reader.readAsArrayBuffer(selectedFile);

        return () => {
            cancelled = true;
            try {
                reader.abort();
            } catch (_) {
                /* ignore */
            }
        };
    }, [selectedFile]);

    // ─── envio ────────────────────────────────────────────────────────────────

    const canSend =
        !!selectedFile &&
        !isParsing &&
        !isSending &&
        !parseError &&
        missingColumns.length === 0 &&
        preCheck.totalRows > 0;

    const handleSend = useCallback(async () => {
        const records = validatedRecordsRef.current;
        if (!records?.length) return;

        setIsSending(true);
        setSendErrors([]);
        setSendResult(null);

        try {
            // remove campo interno de controle antes de enviar
            const payload = records.map(({ __rowNumber, ...rest }) => rest);

            const resp = await bulkSapProductBranch(payload);

            // normaliza resposta — o intermediário pode envolver em body
            const result = resp?.body ?? resp;

            const errors = Array.isArray(result?.errors) ? result.errors : [];
            const inserted = result?.inserted ?? 0;
            const updated = result?.updated ?? 0;

            setSendResult({ inserted, updated, total: inserted + updated });
            setSendErrors(errors);

            // só chama onSuccess se pelo menos um registro foi salvo
            if (inserted + updated > 0) {
                onSuccess?.();
            }
        } catch (e) {
            setSendErrors([
                {
                    index: -1,
                    sap_code: '',
                    branch_id: '',
                    message: e?.message || 'Falha ao enviar a planilha.',
                },
            ]);
        } finally {
            setIsSending(false);
        }
    }, [onSuccess]);

    // ─── render ───────────────────────────────────────────────────────────────

    return (
        <SimplePopup
            width={950}
            height={680}
            outlet={
                <div
                    style={{
                        padding: '10px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* cabeçalho */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '20px',
                            paddingBottom: '15px',
                            borderBottom: '2px solid #e0e0e0',
                            flexShrink: 0,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <h2
                                style={{
                                    fontSize: '24px',
                                    color: '#333',
                                    fontWeight: 600,
                                    margin: 0,
                                    letterSpacing: '-0.5px',
                                }}
                            >
                                Importar vínculos por planilha
                            </h2>
                            <p
                                style={{ fontSize: '14px', color: '#666', margin: '5px 0 0 0' }}
                            >
                                Envie um Excel com as colunas: <b>sap_code</b>, <b>branch_id</b>
                                , <b>layout</b> e <b>product_id</b> (opcional).
                            </p>
                        </div>
                    </div>

                    {/* corpo */}
                    <div style={{ flex: 1, overflow: 'auto' }}>
                        <SpreadsheetUploadValidationModal
                            open={validationOpen}
                            onClose={() => setValidationOpen(false)}
                            missingColumns={missingColumns}
                            fileName={validationMeta?.fileName}
                            sheetName={validationMeta?.sheetName}
                            dataValidationMessage={
                                missingColumns.length === 0 ? parseError : ''
                            }
                        />

                        <DropZone
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const file = e.dataTransfer?.files?.[0];
                                onSelectFile(file);
                            }}
                        >
                            <HiddenInput
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPT}
                                onChange={(e) => {
                                    const file = e.target?.files?.[0];
                                    onSelectFile(file);
                                }}
                            />

                            <DropZoneInner>
                                <DropIcon>
                                    <AiOutlineCloudUpload size={22} color={BrighterGreen} />
                                </DropIcon>
                                <DropText>
                                    <DropTitle>Arraste e solte o Excel aqui</DropTitle>
                                    <DropHint>
                                        ou clique para selecionar. Tipos aceitos: <b>.xlsx</b> e{' '}
                                        <b>.xls</b>
                                    </DropHint>
                                </DropText>
                            </DropZoneInner>

                            <MetaRow>
                                <FilePill
                                    title={selectedFile?.name || 'Nenhum arquivo selecionado'}
                                >
                                    <AiOutlineFileExcel size={18} color={BrighterGreen} />
                                    <FileName>
                                        {selectedFile?.name || 'Nenhum arquivo selecionado'}
                                    </FileName>
                                </FilePill>
                                <FileSize>
                                    {selectedFile ? formatBytes(selectedFile.size) : ''}
                                </FileSize>
                                <div style={{ flex: 1 }} />
                                <SecondaryButton
                                    type="button"
                                    disabled={isSending}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        fileInputRef.current?.click();
                                    }}
                                >
                                    Escolher arquivo
                                </SecondaryButton>
                            </MetaRow>
                        </DropZone>

                        {/* pré-check */}
                        <div style={{ marginTop: 12 }}>
                            <CardTitle>Pré-check (visual)</CardTitle>
                            <PreCheckBox>
                                {!selectedFile ? (
                                    <EmptyPreview>
                                        Selecione um arquivo para visualizar.
                                    </EmptyPreview>
                                ) : null}

                                {selectedFile && isParsing ? (
                                    <EmptyPreview>
                                        {precheckProgress.stage || 'Lendo planilha…'}
                                        {precheckProgress.total > 0
                                            ? ` ${precheckProgress.current}/${precheckProgress.total}`
                                            : ''}
                                    </EmptyPreview>
                                ) : null}

                                {selectedFile && !isParsing && parseError ? (
                                    <PreCheckError style={{ padding: 12 }}>
                                        {parseError}
                                    </PreCheckError>
                                ) : null}

                                {selectedFile && !isParsing && !parseError ? (
                                    preCheck.headers.length > 0 && preCheck.rows.length > 0 ? (
                                        <PreviewTableWrap>
                                            <PreviewMeta>
                                                Total de linhas: <b>{preCheck.totalRows}</b>
                                            </PreviewMeta>
                                            <PreviewTable>
                                                <thead>
                                                    <tr>
                                                        {preCheck.headers.map((h, idx) => (
                                                            <th key={`${idx}-${h}`}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {preCheck.rows.map((row, rIdx) => (
                                                        <tr key={rIdx}>
                                                            {row.map((cell, cIdx) => (
                                                                <td key={cIdx} title={cell}>
                                                                    {cell}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </PreviewTable>
                                        </PreviewTableWrap>
                                    ) : !parseError ? (
                                        <EmptyPreview>
                                            Nenhuma linha para pré-visualizar (verifique se há
                                            cabeçalho e dados na primeira aba).
                                        </EmptyPreview>
                                    ) : null
                                ) : null}
                            </PreCheckBox>
                        </div>

                        {/* resultado do envio */}
                        {isSending ? (
                            <div style={{ textAlign: 'center', padding: '16px 0' }}>
                                <Spinner />
                                <p style={{ marginTop: 8, color: '#666', fontSize: 13 }}>
                                    Enviando registros…
                                </p>
                            </div>
                        ) : null}

                        {!isSending && sendResult ? (
                            <div
                                style={{
                                    marginTop: 12,
                                    padding: '10px 14px',
                                    borderRadius: 6,
                                    backgroundColor: '#f0faf0',
                                    border: '1px solid #b2dfb2',
                                    fontSize: 13,
                                    color: '#2e7d32',
                                }}
                            >
                                Envio concluído: <b>{sendResult.inserted}</b>{' '}
                                {sendResult.inserted === 1
                                    ? 'registro inserido'
                                    : 'registros inseridos'}
                                , <b>{sendResult.updated}</b>{' '}
                                {sendResult.updated === 1 ? 'atualizado' : 'atualizados'}.
                            </div>
                        ) : null}

                        {!isSending && sendErrors.length > 0 ? (
                            <div
                                style={{
                                    marginTop: 12,
                                    padding: '10px 14px',
                                    borderRadius: 6,
                                    backgroundColor: '#fff5f5',
                                    border: '1px solid #f5c2c2',
                                    fontSize: 13,
                                    color: '#b00020',
                                }}
                            >
                                <b>
                                    {sendErrors.length}{' '}
                                    {sendErrors.length === 1
                                        ? 'registro com erro:'
                                        : 'registros com erro:'}
                                </b>
                                <ul style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
                                    {sendErrors.map((err, i) => (
                                        <li key={i}>
                                            {err.sap_code && err.branch_id
                                                ? `${err.sap_code}/${err.branch_id} — `
                                                : ''}
                                            {err.message}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </div>

                    {/* rodapé */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 10,
                            paddingTop: 15,
                            marginTop: 10,
                            borderTop: '1px solid #e0e0e0',
                            flexShrink: 0,
                        }}
                    >
                        <SecondaryButton
                            type="button"
                            disabled={isSending}
                            onClick={onClose}
                        >
                            {sendResult ? 'Fechar' : 'Cancelar'}
                        </SecondaryButton>

                        <PrimaryButton
                            type="button"
                            disabled={!canSend}
                            title={
                                !selectedFile
                                    ? 'Selecione um arquivo'
                                    : missingColumns.length > 0
                                        ? 'Corrija as colunas faltantes'
                                        : isParsing
                                            ? 'Aguarde a validação terminar'
                                            : 'Enviar registros'
                            }
                            onClick={handleSend}
                        >
                            {isSending ? 'Enviando…' : 'Enviar'}
                        </PrimaryButton>
                    </div>
                </div>
            }
        />
    );
};
